import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { estateApi, propertyUnitApi } from "../services/api";
import { getCurrentEstate, isEstateAdmin } from "../utils/permissions";
import {
  Plus,
  Edit,
  Home,
  Building2,
  Search,
  Filter,
  RefreshCcw,
  UserCheck,
  X,
} from "lucide-react";
import toast from "react-hot-toast";
import { useEstate } from "../contexts/EstateContext";

const buildEmptyFormData = (estateId = "") => ({
  estate: estateId,
  unitCode: "",
  displayName: "",
  block: "",
  addressLine: "",
  propertyType: "apartment",
  status: "active",
  occupancyStatus: "vacant",
  notes: "",
});

const getUnitBadgeClass = (value, type = "status") => {
  const normalized = (value || "").toLowerCase();

  if (type === "status") {
    if (normalized === "active") return "badge badge-success";
    return "badge badge-danger";
  }

  if (normalized === "vacant") return "badge badge-success";
  if (normalized === "reserved") return "badge badge-warning";
  if (normalized === "occupied") return "badge badge-info";
  return "badge badge-danger";
};

const PropertyUnitsPage = () => {
  const { selectedEstateId: shellEstateId, setSelectedEstateId: setShellEstateId } = useEstate();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const preselectedEstateId = searchParams.get("estateId") || "";
  const currentEstate = getCurrentEstate();
  const estateAdmin = isEstateAdmin();

  const [selectedEstateId, setSelectedEstateId] = useState(
    preselectedEstateId || currentEstate || "",
  );
  const [statusFilter, setStatusFilter] = useState("active");
  const [occupancyFilter, setOccupancyFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState("create");
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [formData, setFormData] = useState(
    buildEmptyFormData(preselectedEstateId || currentEstate || ""),
  );

  const { data: estates = [], isLoading: loadingEstates } = useQuery(
    "estates",
    estateApi.getAll,
    {
      retry: 1,
    },
  );

  useEffect(() => {
    if (shellEstateId && shellEstateId !== selectedEstateId) {
      setSelectedEstateId(shellEstateId);
      setFormData((prev) => ({ ...prev, estate: shellEstateId }));
      return;
    }
    if (shellEstateId) return;
    if (preselectedEstateId) {
      setSelectedEstateId(preselectedEstateId);
      setFormData((prev) => ({
        ...prev,
        estate: preselectedEstateId,
      }));
      return;
    }

    if (selectedEstateId) return;

    if (estateAdmin && currentEstate) {
      setSelectedEstateId(currentEstate);
      setFormData((prev) => ({ ...prev, estate: currentEstate }));
      return;
    }

    if (estates.length === 1) {
      setSelectedEstateId(estates[0]._id);
      setFormData((prev) => ({ ...prev, estate: estates[0]._id }));
    }
  }, [
    preselectedEstateId,
    shellEstateId,
    selectedEstateId,
    estateAdmin,
    currentEstate,
    estates,
  ]);

  const unitQueryParams = useMemo(() => {
    const params = {};
    if (selectedEstateId) params.estateId = selectedEstateId;
    if (statusFilter !== "all") params.status = statusFilter;
    if (occupancyFilter !== "all") params.occupancyStatus = occupancyFilter;
    if (searchTerm.trim()) params.unitCode = searchTerm.trim();
    return params;
  }, [selectedEstateId, statusFilter, occupancyFilter, searchTerm]);

  const {
    data: propertyUnits = [],
    isLoading,
    refetch,
  } = useQuery(
    ["propertyUnits", unitQueryParams],
    () => propertyUnitApi.getAll(unitQueryParams),
    {
      retry: 1,
    },
  );

  const filteredUnits = Array.isArray(propertyUnits) ? propertyUnits : [];

  const unitStats = useMemo(() => {
    const stats = {
      total: filteredUnits.length,
      vacant: 0,
      reserved: 0,
      occupied: 0,
    };

    filteredUnits.forEach((unit) => {
      const occupancy = (unit?.occupancyStatus || "").toLowerCase();
      if (occupancy === "vacant") stats.vacant += 1;
      if (occupancy === "reserved") stats.reserved += 1;
      if (occupancy === "occupied") stats.occupied += 1;
    });

    return stats;
  }, [filteredUnits]);

  const resetForm = (estateIdOverride = selectedEstateId || "") => {
    setSelectedUnit(null);
    setFormData(buildEmptyFormData(estateIdOverride));
  };

  const createUnit = useMutation((data) => propertyUnitApi.create(data), {
    onSuccess: () => {
      queryClient.invalidateQueries(["propertyUnits"]);
      queryClient.invalidateQueries(["residentPropertyUnits"]);
      toast.success("Property unit created successfully");
      setShowModal(false);
      resetForm();
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Failed to create property unit",
      );
    },
  });

  const updateUnit = useMutation(
    (data) => propertyUnitApi.update(selectedUnit._id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["propertyUnits"]);
        queryClient.invalidateQueries(["residentPropertyUnits"]);
        toast.success("Property unit updated successfully");
        setShowModal(false);
        resetForm();
      },
      onError: (error) => {
        toast.error(
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Failed to update property unit",
        );
      },
    },
  );

  const handleCreate = () => {
    setModalType("create");
    resetForm(selectedEstateId || currentEstate || "");
    setShowModal(true);
  };

  const handleEdit = (unit) => {
    setModalType("edit");
    setSelectedUnit(unit);
    setFormData({
      estate: unit.estate?._id || unit.estate || "",
      unitCode: unit.unitCode || "",
      displayName: unit.displayName || "",
      block: unit.block || "",
      addressLine: unit.addressLine || "",
      propertyType: unit.propertyType || "apartment",
      status: unit.status || "active",
      occupancyStatus: unit.occupancyStatus || "vacant",
      notes: unit.notes || "",
    });
    setShowModal(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const payload = {
      estate: formData.estate,
      unitCode: formData.unitCode.trim(),
      displayName: formData.displayName.trim(),
      block: formData.block.trim(),
      addressLine: formData.addressLine.trim(),
      propertyType: formData.propertyType,
      status: formData.status,
      occupancyStatus: formData.occupancyStatus,
      notes: formData.notes.trim(),
    };

    if (!payload.estate || !payload.unitCode) {
      toast.error("Estate and unit code are required");
      return;
    }

    if (modalType === "create") {
      createUnit.mutate(payload);
      return;
    }

    updateUnit.mutate(payload);
  };

  const selectedEstateName =
    estates.find((estate) => estate._id === selectedEstateId)?.name || "";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title">Units</h1>
          <p className="text-gray-600">
            Manage the estate unit register used during owner and tenant
            onboarding.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate("/users")}
            className="btn btn-secondary"
          >
            Go to Users
          </button>
          <button
            type="button"
            onClick={() => refetch()}
            className="btn btn-secondary flex items-center"
          >
            <RefreshCcw className="mr-2 h-4 w-4" />
            Refresh
          </button>
          <button
            type="button"
            onClick={handleCreate}
            className="btn btn-primary flex items-center"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Unit
          </button>
        </div>
      </div>

      <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900">
        Resident onboarding reads from this register. A unit only needs to be
        in the correct estate and marked <span className="font-semibold">active</span>.
        Final onboarding availability is still validated against active
        residents and pending registrations.
      </div>

      {selectedEstateId && (
        <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
          <span className="font-medium text-slate-900">
            Current estate:
          </span>{" "}
          {selectedEstateName || "Selected estate"}
          <span className="mx-2 text-slate-300">|</span>
          Add or update units here, then return to the user registration form
          to complete owner or tenant onboarding.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-gray-500">Units Loaded</p>
            <p className="text-2xl font-bold text-gray-900">
              {unitStats.total}
            </p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-gray-500">Vacant</p>
            <p className="text-2xl font-bold text-emerald-600">
              {unitStats.vacant}
            </p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-gray-500">Reserved</p>
            <p className="text-2xl font-bold text-amber-600">
              {unitStats.reserved}
            </p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-gray-500">Occupied</p>
            <p className="text-2xl font-bold text-blue-600">
              {unitStats.occupied}
            </p>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="form-label flex items-center">
                <Building2 className="h-4 w-4 mr-2" />
                Estate
              </label>
              <select
                className="form-input"
                value={selectedEstateId}
                onChange={(e) => { setSelectedEstateId(e.target.value); setShellEstateId(e.target.value); }}
                disabled={loadingEstates}
              >
                <option value="">All Accessible Estates</option>
                {Array.isArray(estates) &&
                  estates.map((estate) => (
                    <option key={estate._id} value={estate._id}>
                      {estate.name}
                    </option>
                  ))}
              </select>
            </div>

            <div>
              <label className="form-label flex items-center">
                <Filter className="h-4 w-4 mr-2" />
                Status
              </label>
              <select
                className="form-input"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div>
              <label className="form-label">Occupancy</label>
              <select
                className="form-input"
                value={occupancyFilter}
                onChange={(e) => setOccupancyFilter(e.target.value)}
              >
                <option value="all">All Occupancy States</option>
                <option value="vacant">Vacant</option>
                <option value="reserved">Reserved</option>
                <option value="occupied">Occupied</option>
              </select>
            </div>

            <div>
              <label className="form-label flex items-center">
                <Search className="h-4 w-4 mr-2" />
                Unit Code
              </label>
              <input
                type="text"
                className="form-input"
                placeholder="e.g. A-101"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Unit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Resident
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center">
                      <div className="spinner mx-auto"></div>
                    </td>
                  </tr>
                ) : filteredUnits.length ? (
                  filteredUnits.map((unit) => (
                    <tr key={unit._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-start">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <Home className="h-5 w-5 text-blue-600" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {unit.unitCode}
                            </div>
                            <div className="text-sm text-gray-500">
                              {unit.displayName || unit.block || "No display name"}
                            </div>
                            {unit.addressLine && (
                              <div className="text-xs text-gray-400 mt-1">
                                {unit.addressLine}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {unit.estate?.name || selectedEstateName || "-"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 capitalize">
                        {unit.propertyType || "-"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col gap-2">
                          <span className={getUnitBadgeClass(unit.status, "status")}>
                            {unit.status}
                          </span>
                          <span
                            className={getUnitBadgeClass(
                              unit.occupancyStatus,
                              "occupancy",
                            )}
                          >
                            {unit.occupancyStatus}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {unit.currentResidentProfile ? (
                          <div className="text-sm text-gray-700 flex items-center">
                            <UserCheck className="h-4 w-4 mr-2 text-green-600" />
                            {unit.currentResidentProfile.fullName}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">None linked</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <button
                          type="button"
                          onClick={() => handleEdit(unit)}
                          className="text-blue-600 hover:text-blue-900 inline-flex items-center"
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center">
                      <Home className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p className="text-gray-600 font-medium">
                        No property units found
                      </p>
                      <p className="text-sm text-gray-500 mt-2">
                        {selectedEstateId
                          ? "This estate has no matching units yet. Add one to continue with resident onboarding."
                          : "Select an estate or add units to begin."}
                      </p>
                      <button
                        type="button"
                        onClick={handleCreate}
                        className="btn btn-primary mt-4"
                      >
                        Add First Unit
                      </button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal max-w-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">
                {modalType === "create" ? "Add Property Unit" : "Edit Property Unit"}
              </h2>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">
                    Estate <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="estate"
                    value={formData.estate}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, estate: e.target.value }))
                    }
                    className="form-input"
                    required
                    disabled={modalType === "edit"}
                  >
                    <option value="">Select estate</option>
                    {Array.isArray(estates) &&
                      estates.map((estate) => (
                        <option key={estate._id} value={estate._id}>
                          {estate.name}
                        </option>
                      ))}
                  </select>
                </div>

                <div>
                  <label className="form-label">
                    Unit Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.unitCode}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, unitCode: e.target.value }))
                    }
                    placeholder="e.g. A-101"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Display Name</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.displayName}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        displayName: e.target.value,
                      }))
                    }
                    placeholder="e.g. Block A Corner Flat"
                  />
                </div>

                <div>
                  <label className="form-label">Block</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.block}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, block: e.target.value }))
                    }
                    placeholder="e.g. Block A"
                  />
                </div>
              </div>

              <div>
                <label className="form-label">Address Line</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.addressLine}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      addressLine: e.target.value,
                    }))
                  }
                  placeholder="Optional precise location"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="form-label">Property Type</label>
                  <select
                    className="form-input"
                    value={formData.propertyType}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        propertyType: e.target.value,
                      }))
                    }
                  >
                    <option value="apartment">Apartment</option>
                    <option value="duplex">Duplex</option>
                    <option value="terrace">Terrace</option>
                    <option value="bungalow">Bungalow</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="form-label">Status</label>
                  <select
                    className="form-input"
                    value={formData.status}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, status: e.target.value }))
                    }
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>

                <div>
                  <label className="form-label">Occupancy</label>
                  <select
                    className="form-input"
                    value={formData.occupancyStatus}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        occupancyStatus: e.target.value,
                      }))
                    }
                  >
                    <option value="vacant">Vacant</option>
                    <option value="reserved">Reserved</option>
                    <option value="occupied">Occupied</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="form-label">Notes</label>
                <textarea
                  className="form-input"
                  rows="3"
                  value={formData.notes}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, notes: e.target.value }))
                  }
                  placeholder="Optional onboarding or occupancy note"
                />
              </div>

              <div className="flex justify-between space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => navigate("/users")}
                  className="btn btn-secondary"
                >
                  Back to Users
                </button>
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createUnit.isLoading || updateUnit.isLoading}
                    className="btn btn-primary"
                  >
                    {createUnit.isLoading || updateUnit.isLoading
                      ? "Saving..."
                      : modalType === "create"
                        ? "Create Unit"
                        : "Save Changes"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyUnitsPage;
